/**
 * Duration entry: `[ 2 ] hours [ 30 ] minutes`.
 *
 * Plan D9. Staff think in hours and minutes; the stored contract is a single
 * integer of minutes (§1.13), because "2.5 hours" is ambiguous in a way "150" is
 * not. The conversion is `toMinutes` from the shared package rather than
 * arithmetic here, so the echo beneath the inputs cannot drift from what is sent
 * — the API, the tests and this control all agree by construction.
 *
 * Two number inputs rather than one free-text field: "1h30", "1.5", "90m" and
 * "1:30" are all things people type into a duration box, and guessing which one
 * they meant is how a volunteer's hours get halved.
 */

import { useId } from 'react';
import { formatDuration, fromMinutes, toMinutes } from '@parinaam-vms/shared';
import styles from './Field.module.css';

export interface DurationInputProps {
  label: string;
  /** The value in minutes — the same unit the API takes. */
  value: number;
  onChange: (minutes: number) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
}

export function DurationInput({
  label,
  value,
  onChange,
  error,
  required,
  disabled,
}: DurationInputProps) {
  const groupId = useId();
  const hoursId = `${groupId}-hours`;
  const minutesId = `${groupId}-minutes`;
  const errorId = `${groupId}-error`;
  const describedBy = error ? errorId : undefined;

  const { hours, minutes } = fromMinutes(value);

  const update = (nextHours: number, nextMinutes: number) => {
    const safeHours = Number.isFinite(nextHours) ? Math.max(0, Math.trunc(nextHours)) : 0;
    const safeMinutes = Number.isFinite(nextMinutes) ? Math.max(0, Math.trunc(nextMinutes)) : 0;
    onChange(toMinutes(safeHours, safeMinutes));
  };

  return (
    <fieldset className={styles.group} aria-describedby={describedBy}>
      <legend className={styles.label}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </legend>

      <div className={styles.duration}>
        <label className={styles.durationPart} htmlFor={hoursId}>
          <input
            id={hoursId}
            type="number"
            min={0}
            max={24}
            inputMode="numeric"
            value={hours}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            className={`${styles.control} ${error ? styles.controlError : ''}`}
            onChange={(event) => update(Number(event.target.value), minutes)}
          />
          <span>hours</span>
        </label>

        <label className={styles.durationPart} htmlFor={minutesId}>
          <input
            id={minutesId}
            type="number"
            min={0}
            // 90 minutes is a legitimate thing to type; it becomes 1h 30m on the
            // next render rather than being rejected.
            max={59}
            inputMode="numeric"
            value={minutes}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            className={`${styles.control} ${error ? styles.controlError : ''}`}
            onChange={(event) => update(hours, Number(event.target.value))}
          />
          <span>minutes</span>
        </label>

        {/* The echo is what staff check before saving: it is the stored value,
            spelled out, not a second opinion computed from the inputs. */}
        <p className={styles.durationEcho} aria-live="polite">
          {`= ${formatDuration(value)} (${value} minutes)`}
        </p>
      </div>

      {error && (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}
